import { useDispatch, useSelector } from "react-redux";
import {
    Search,
    Filter,
    Check,
    X,
    Calendar,
    Clock,
    AlertCircle,
    FileText,
    ChevronDown,
    DollarSign,
    Receipt,
    Download,
    Loader,
    User,
    Briefcase,
    RotateCcw,
    Eye,
} from "lucide-react";
import {
    getManagerNoteDeFraisInboxRequest,
    getManagerNoteDeFraisHistoryRequest,
    managerNoteDeFraisDecisionRequest,
} from "../../../../redux/actions/noteDeFraisRequestActions";
import useTreatmentDelegation from "./useTreatmentDelegation";
import "../TraitementStyles/TraitementNotesFrais.css";
import { useEffect, useMemo, useRef, useState } from "react";
import { API_BASE_URL } from "../../../../../api";

const SearchableCombobox = ({ value, onChange, options, placeholder }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState(value || "");
    const wrapperRef = useRef(null);

    useEffect(() => {
        setQuery(value || "");
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredOptions = useMemo(() => {
        if (!query) return options;
        const q = query.toLowerCase();
        return options.filter((o) => o.label.toLowerCase().includes(q));
    }, [options, query]);

    const handleSelect = (opt) => {
        setQuery(opt.label);
        onChange(opt.value);
        setOpen(false);
    };

    const handleInputChange = (e) => {
        setQuery(e.target.value);
        onChange(e.target.value);
        setOpen(true);
    };

    const handleClear = () => {
        setQuery("");
        onChange("");
        setOpen(false);
    };

    return (
        <div ref={wrapperRef} style={{ position: "relative", width: "100%" }}>
            <div style={{ position: "relative" }}>
                <input
                    type="text"
                    className="dcInput"
                    style={{ width: "100%", fontSize: "13px", paddingRight: 56 }}
                    placeholder={placeholder}
                    value={query}
                    onChange={handleInputChange}
                    onFocus={() => setOpen(true)}
                />
                <div style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", gap: 2 }}>
                    {query && (
                        <button type="button" onClick={handleClear} style={{ border: "none", background: "none", cursor: "pointer", color: "#9ca3af", padding: 4, lineHeight: 0 }} title="Effacer">
                            <X size={13} />
                        </button>
                    )}
                    <button type="button" onClick={() => setOpen(o => !o)} style={{ border: "none", background: "none", cursor: "pointer", color: "#9ca3af", padding: 4, lineHeight: 0 }} title="Afficher la liste">
                        <ChevronDown size={14} />
                    </button>
                </div>
            </div>
            {open && filteredOptions.length > 0 && (
                <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.10)", zIndex: 30, maxHeight: 220, overflowY: "auto" }}>
                    {filteredOptions.map((opt) => {
                        const isSelected = opt.value === value;
                        return (
                            <div key={opt.value} onClick={() => handleSelect(opt)}
                                style={{ padding: "8px 12px", fontSize: 13, cursor: "pointer", background: isSelected ? "#eff6ff" : "#fff", color: isSelected ? "#204385" : "#374151", fontWeight: isSelected ? 600 : 400 }}
                                onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                                onMouseLeave={(e) => (e.currentTarget.style.background = isSelected ? "#eff6ff" : "#fff")}>
                                {opt.label}
                            </div>
                        );
                    })}
                </div>
            )}
            {open && query && filteredOptions.length === 0 && (
                <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.10)", zIndex: 30, padding: "8px 12px", fontSize: 12, color: "#9ca3af" }}>
                    Aucun résultat
                </div>
            )}
        </div>
    );
};

const TraitementNotesFraisManager = () => {
    const dispatch = useDispatch();

    // Redux state
    const { managerInbox, managerHistory, loading } = useSelector((state) => state.noteDeFraisRequests);
    const user = useSelector((state) => state.auth.user);

    const [activeTab, setActiveTab] = useState("enCours");
    const [filterCollab, setFilterCollab] = useState("");
    const [filterType, setFilterType] = useState("");
    const [filterDate, setFilterDate] = useState("");

    const handleResetFilters = () => {
        setFilterCollab("");
        setFilterType("");
        setFilterDate("");
    };

    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedNote, setSelectedNote] = useState(null);
    const [motifRejet, setMotifRejet] = useState("");

    const delegation = useTreatmentDelegation({
        endpoint: "/api/note-frais-requests/manager/treatment-delegation",
        label: "de notes de frais",
        onUpdated: () => {
            dispatch(getManagerNoteDeFraisInboxRequest());
            dispatch(getManagerNoteDeFraisHistoryRequest());
        },
    });

    // Charger les données au montage
    useEffect(() => {
        dispatch(getManagerNoteDeFraisInboxRequest());
        dispatch(getManagerNoteDeFraisHistoryRequest());
    }, [dispatch]);

    const formatDate = (dateStr) => {
        if (!dateStr) return "-";
        const date = new Date(dateStr);
        return date.toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "short",
            year: "numeric"
        });
    };

    const formatMontant = (montant, devise = "MAD") => {
        return new Intl.NumberFormat("fr-MA", {
            style: "decimal",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(montant || 0) + " dhs";
    };

    const getDownloadUrl = (path) =>
        `${API_BASE_URL}/api/files/download?path=${encodeURIComponent(path || "")}`;

    const getStatutBadge = (statut) => {
        const config = {
            PENDING_MANAGER: { label: "En attente", bg: "#fff3cd", color: "#856404" },
            PENDING_ADMIN: { label: "En attente de l'admin RH", bg: "#d1ecf1", color: "#0c5460" },
            APPROVED: { label: "Approuvé", bg: "#d4edda", color: "#155724" },
            REJECTED: { label: "Rejeté", bg: "#f8d7da", color: "#721c24" },
            PAID: { label: "Remboursé", bg: "#d4edda", color: "#155724" },
        };
        const { label, bg, color } = config[statut] || config.PENDING_MANAGER;
        return (
            <span className="tnfBadge" style={{ backgroundColor: bg, color }}>
                {label}
            </span>
        );
    };

    const handleApprove = (noteId) => {
        dispatch(managerNoteDeFraisDecisionRequest({
            id: noteId,
            decision: true,
            comment: "",
        }));

        if (showDetailsModal) {
            closeDetails();
        }

        setTimeout(() => {
            dispatch(getManagerNoteDeFraisInboxRequest());
            dispatch(getManagerNoteDeFraisHistoryRequest());
        }, 800);
    };

    const handleReject = (note) => {
        setSelectedNote(note);
        setShowRejectModal(true);
    };

    const confirmReject = () => {
        if (!motifRejet.trim() || !selectedNote) {
            alert("Veuillez saisir un motif de rejet");
            return;
        }

        dispatch(managerNoteDeFraisDecisionRequest({
            id: selectedNote.id,
            decision: false,
            comment: motifRejet,
        }));

        setShowRejectModal(false);
        setMotifRejet("");
        setSelectedNote(null);

        if (showDetailsModal) {
            closeDetails();
        }

        setTimeout(() => {
            dispatch(getManagerNoteDeFraisInboxRequest());
            dispatch(getManagerNoteDeFraisHistoryRequest());
        }, 1000);
    };

    const showDetails = (note) => {
        setSelectedNote(note);
        setShowDetailsModal(true);
    };

    const closeDetails = () => {
        setShowDetailsModal(false);
        setSelectedNote(null);
    };

    const closeReject = () => {
        setShowRejectModal(false);
        setMotifRejet("");
        setSelectedNote(null);
    };

    // Combiner les données selon l'onglet actif
    const allNotes = useMemo(() => {
        if (activeTab === "enCours") {
            return (managerInbox || []).filter(note => note.status === "PENDING_MANAGER");
        }

        return (managerHistory || []).filter(n => {
            if (activeTab === "approuve") return n.status === "APPROVED" || n.status === "PAID" || n.status === "PENDING_ADMIN";
            if (activeTab === "rejete") return n.status === "REJECTED";
            return true;
        });
    }, [activeTab, managerInbox, managerHistory]);

    const collaborateurOptions = useMemo(() => {
        const map = new Map();
        (allNotes || []).forEach((n) => {
            const name = n.employeeName || "";
            if (name && !map.has(name)) {
                map.set(name, { value: name, label: n.employeeMatricule ? `${name} (${n.employeeMatricule})` : name });
            }
        });
        return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
    }, [allNotes]);

    const typeOptions = useMemo(() => {
        return [...new Set((allNotes || []).map((n) => n.category || n.requestType).filter(Boolean))]
            .sort().map((t) => ({ value: t, label: t }));
    }, [allNotes]);

    const getFilteredNotes = () => {
        let filtered = allNotes;

        if (filterCollab) {
            const val = filterCollab.toLowerCase();
            filtered = filtered.filter((n) =>
                (n.employeeName || "").toLowerCase().includes(val) ||
                (n.employeeMatricule || "").toLowerCase().includes(val)
            );
        }
        if (filterType) {
            const val = filterType.toLowerCase();
            filtered = filtered.filter((n) =>
                ((n.category || n.requestType) || "").toLowerCase().includes(val)
            );
        }
        if (filterDate) {
            filtered = filtered.filter((n) => {
                if (!n.dateDepense && !n.createdAt) return false;
                const dStr = n.dateDepense || n.createdAt;
                return String(dStr).slice(0, 10) === filterDate;
            });
        }

        return filtered;
    };

    const tabs = useMemo(() => {
        const enCoursCount = (managerInbox || []).filter(note => note.status === "PENDING_MANAGER").length;
        const approuveCount = (managerHistory || []).filter(n =>
            n.status === "APPROVED" || n.status === "PAID" || n.status === "PENDING_ADMIN"
        ).length;
        const rejeteCount = (managerHistory || []).filter(n => n.status === "REJECTED").length;

        return [
            { id: "enCours", label: "En attente", count: enCoursCount },
            { id: "approuve", label: "Approuvées", count: approuveCount },
            { id: "rejete", label: "Rejetées", count: rejeteCount },
        ];
    }, [managerInbox, managerHistory]);

    const typesDepense = useMemo(() => {
        const types = new Set();
        allNotes.forEach(n => {
            if (n.category || n.requestType) types.add(n.category || n.requestType);
        });
        return Array.from(types);
    }, [allNotes]);

    const totalMontant = useMemo(
        () => getFilteredNotes().reduce((sum, n) => sum + (n.montantDepense || 0), 0),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [allNotes, filterCollab, filterType, filterDate]
    );

    const urgentCount = useMemo(() => {
        return managerInbox?.filter(n => n.urgent && n.status === "PENDING_MANAGER").length || 0;
    }, [managerInbox]);

    const filteredNotes = getFilteredNotes();

    const getCouleurByCategorie = (categorie) => {
        const colors = {
            TRANSPORT: "#3b82f6",
            HEBERGEMENT: "#8b5cf6",
            RESTAURATION: "#10b981",
            AUTRE: "#f59e0b",
        };
        return colors[categorie] || "#6b7280";
    };

    if (loading && allNotes.length === 0) {
        return (
            <div className="tnfPage">
                <div className="tnfWrap" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
                    <Loader size={32} className="spin" />
                </div>
            </div>
        );
    }

    return (
        <div className="tnfPage">
            <div className="tnfWrap">
                {/* Header */}
                <div className="tnfHeader" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                    <div>
                        <h1>Traitement des notes de frais</h1>
                        <p>Validez les notes de frais de votre équipe</p>
                    </div>
                    <div className="tavDelegationSetting">
                        <div>
                            <div className="tavDelegationSetting__title">Délégation Admin RH</div>
                            <div className="tavDelegationSetting__sub">Transmettre mon traitement des notes de frais à l'admin RH</div>
                        </div>
                        <label className="tavSwitch" title="Délégation Admin RH">
                            <input
                                type="checkbox"
                                checked={delegation.enabled}
                                disabled={delegation.loading}
                                onChange={(e) => delegation.toggle(e.target.checked)}
                            />
                            <span />
                        </label>
                    </div>
                </div>

                {/* Stats */}
                {/*<div className="tnfStats">*/}
                {/*    <div className="tnfStatCard">*/}
                {/*        <div>*/}
                {/*            <div className="tnfStatLabel">En attente</div>*/}
                {/*            <div className="tnfStatValue">*/}
                {/*                {(managerInbox || []).filter(note => note.status === "PENDING_MANAGER").length || 0}*/}
                {/*            </div>*/}
                {/*        </div>*/}
                {/*        <Clock size={28} className="tnfStatIcon tnfStatIcon--warn" />*/}
                {/*    </div>*/}

                {/*    <div className="tnfStatCard">*/}
                {/*        <div>*/}
                {/*            <div className="tnfStatLabel">Montant total</div>*/}
                {/*            <div className="tnfStatValue tnfStatValue--money">{formatMontant(totalMontant)}</div>*/}
                {/*        </div>*/}
                {/*        <DollarSign size={28} className="tnfStatIcon tnfStatIcon--accent" />*/}
                {/*    </div>*/}

                {/*    {urgentCount > 0 && (*/}
                {/*        <div className="tnfStatCard">*/}
                {/*            <div>*/}
                {/*                <div className="tnfStatLabel">Urgentes</div>*/}
                {/*                <div className="tnfStatValue tnfStatValue--orange">{urgentCount}</div>*/}
                {/*            </div>*/}
                {/*            <AlertCircle size={28} className="tnfStatIcon tnfStatIcon--orange" />*/}
                {/*        </div>*/}
                {/*    )}*/}

                {/*    <div className="tnfStatCard">*/}
                {/*        <div>*/}
                {/*            <div className="tnfStatLabel">Approuvées</div>*/}
                {/*            <div className="tnfStatValue tnfStatValue--ok">*/}
                {/*                {managerHistory?.filter(n =>*/}
                {/*                    n.status === "APPROVED" || n.status === "PAID" || n.status === "PENDING_ADMIN"*/}
                {/*                ).length || 0}*/}
                {/*            </div>*/}
                {/*        </div>*/}
                {/*        <Check size={28} className="tnfStatIcon tnfStatIcon--ok" />*/}
                {/*    </div>*/}

                {/*    <div className="tnfStatCard">*/}
                {/*        <div>*/}
                {/*            <div className="tnfStatLabel">Rejetées</div>*/}
                {/*            <div className="tnfStatValue tnfStatValue--no">*/}
                {/*                {managerHistory?.filter(n => n.status === "REJECTED").length || 0}*/}
                {/*            </div>*/}
                {/*        </div>*/}
                {/*        <X size={28} className="tnfStatIcon tnfStatIcon--no" />*/}
                {/*    </div>*/}
                {/*</div>*/}

                {/* Filters Panel */}
                <div className="tnfCard" style={{ overflow: "visible" }}>
                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: "16px", padding: "4px" }}>
                        <div style={{ minWidth: 220, flex: "1 1 220px" }}>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>Collaborateur / Matricule</label>
                            <SearchableCombobox value={filterCollab} onChange={setFilterCollab} options={collaborateurOptions} placeholder="Nom ou matricule..." />
                        </div>
                        <div style={{ minWidth: 200, flex: "1 1 200px" }}>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>Type de dépense</label>
                            <SearchableCombobox value={filterType} onChange={setFilterType} options={typeOptions} placeholder="Rechercher un type..." />
                        </div>
                        <div style={{ minWidth: 160, flex: "1 1 160px" }}>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>Date de demande</label>
                            <input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} className="dcInput" style={{ width: "100%", fontSize: "13px" }} />
                        </div>
                        <div style={{ display: "flex", gap: 8, flexShrink: 0, alignItems: "flex-end" }}>
                            <button type="button" onClick={handleResetFilters} className="dcBtn dcBtn--outline" style={{ padding: "10px 12px", display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", height: "38px", whiteSpace: "nowrap" }}>
                                <RotateCcw size={14} /> Réinitialiser
                            </button>
                        </div>
                    </div>
                </div>

                {/* Tabs + Table */}
                <div className="tnfCard">
                    <div className="tnfTabs">
                        {tabs.map((tab) => (
                            <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)} className={`tnfTab ${activeTab === tab.id ? "is-active" : ""}`}>
                                {tab.label}
                                <span className={`tnfCount ${activeTab === tab.id ? "is-active" : ""}`}>{tab.count}</span>
                            </button>
                        ))}
                    </div>

                    <div className="tnfTableWrap">
                        {filteredNotes.length === 0 ? (
                            <div className="tnfEmpty">
                                <AlertCircle size={46} />
                                <div>Aucune note de frais trouvée</div>
                            </div>
                        ) : (
                            <table className="tnfTable">
                                <thead>
                                    <tr>
                                        <th>Collaborateur</th>
                                        <th>Catégorie</th>
                                        <th>Description</th>
                                        {/*<th>Centre de coût</th>*/}
                                        <th>Date de la dépense</th>
                                        <th>Montant</th>
                                        <th>Justificatif</th>
                                        {/*<th>Statut</th>*/}
                                        <th className="is-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredNotes.map((note) => (
                                        <tr
                                            key={note.id}
                                            className={`${note.urgent ? "tnfRowUrgent" : ""}`}
                                            title={note.urgent ? "Note urgente" : ""}
                                        >
                                            <td>
                                                <div className="tnfName">
                                                    <span className="tnfPrimary">
                                                        {note.employeeName || "N/A"}
                                                    </span>
                                                    {note.urgent && <span className="tnfFire">🔥</span>}
                                                </div>
                                                <div className="tnfSub">
                                                    {note.employeeMatricule || "-"}
                                                </div>
                                            </td>

                                            <td>
                                                <div className="tnfType">
                                                    <span
                                                        className="tnfDot"
                                                        style={{ backgroundColor: getCouleurByCategorie(note.category || note.requestType) }}
                                                    />
                                                    <span className="tnfPrimary">
                                                        {note.category || note.requestType || "-"}
                                                    </span>
                                                </div>
                                            </td>

                                            <td className="tnfDesc">
                                                <div className="tnfTrunc" title={note.description}>
                                                    {note.description || "-"}
                                                </div>
                                            </td>

                                            <td><span className="tnfMuted">{formatDate(note.dateDepense)}</span></td>

                                            <td>
                                                <div className="tnfAmount">
                                                    <div className="tnfPrimary tnfAmountMain">{formatMontant(note.montantDepense)}</div>
                                                </div>
                                            </td>

                                            <td>
                                                <div className="tnfJustif">
                                                    <span>{note.justificatifs?.length || '-'}</span>
                                                </div>
                                            </td>

                                            {/*<td>{getStatutBadge(note.status)}</td>*/}

                                            <td className="is-right" onClick={(e) => e.stopPropagation()}>
                                                <div className="tnfRowActions">
                                                    {activeTab === "enCours" && (
                                                        <>
                                                            <button
                                                                type="button"
                                                                className="tnfActionBtn tnfActionBtn--ok"
                                                                onClick={() => handleApprove(note.id)}
                                                                title="Approuver"
                                                                disabled={loading}
                                                            >
                                                                <Check size={16} />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="tnfActionBtn tnfActionBtn--no"
                                                                onClick={() => handleReject(note)}
                                                                title="Refuser"
                                                                disabled={loading}
                                                            >
                                                                <X size={16} />
                                                            </button>
                                                        </>
                                                    )}
                                                    <button
                                                        type="button"
                                                        className="tnfIconBtn"
                                                        onClick={() => showDetails(note)}
                                                        title="Voir détails"
                                                    >
                                                        <Eye size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {/* DETAILS MODAL */}
            {showDetailsModal && selectedNote && (
                <div className="tnfModal">
                    <div className="tnfModal__panel" role="dialog" aria-modal="true">
                        <div className="tnfModal__header">
                            <div className="tnfModal__headLeft">
                                <div className="tnfModal__icon">
                                    <FileText size={18} />
                                </div>
                                <div>
                                    <div className="tnfModal__title">Détails de la note de frais</div>
                                    <div className="tnfModal__sub">
                                        {selectedNote.employeeName || "N/A"}
                                    </div>
                                </div>
                            </div>

                            <button type="button" className="tnfIconBtn" onClick={closeDetails} aria-label="Fermer">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tnfModal__body">
                            {/* Alerte urgence */}
                            {selectedNote.urgent && (
                                <div className="tnfAlert tnfAlert--orange">
                                    <AlertCircle size={18} />
                                    <div>
                                        <div className="tnfAlert__title">Note de frais urgente</div>
                                        <div className="tnfAlert__text">Cette demande nécessite un traitement prioritaire</div>
                                    </div>
                                </div>
                            )}

                            {/* Informations collaborateur */}
                            <div className="tnfPanel">
                                <div className="tnfPanel__title">Informations collaborateur</div>
                                <div className="tnfGrid2">
                                    <div className="tnfInfo">
                                        <div className="tnfInfo__label">Nom complet</div>
                                        <div className="tnfInfo__value">
                                            {selectedNote.employeeName || "-"}
                                        </div>
                                    </div>
                                    <div className="tnfInfo">
                                        <div className="tnfInfo__label">Matricule</div>
                                        <div className="tnfInfo__value">{selectedNote.employeeMatricule || "-"}</div>
                                    </div>
                                    <div className="tnfInfo">
                                        <div className="tnfInfo__label">ID Employé</div>
                                        <div className="tnfInfo__value">{selectedNote.employeeId || "-"}</div>
                                    </div>
                                    {/*<div className="tnfInfo">*/}
                                    {/*    <div className="tnfInfo__label">Centre de coût</div>*/}
                                    {/*    <div className="tnfInfo__value">{selectedNote.centreCouts || "-"}</div>*/}
                                    {/*</div>*/}
                                </div>
                            </div>

                            {/* Détails dépense */}
                            <div className="tnfBlock">
                                <div className="tnfBlock__title">Détails de la dépense</div>

                                <div className="tnfLine">
                                    <Receipt size={16} />
                                    <div className="tnfLine__content">
                                        <div className="tnfLine__label">Catégorie</div>
                                        <div className="tnfLine__value">
                                            {selectedNote.category || selectedNote.requestType || "-"}
                                        </div>
                                    </div>
                                </div>

                                <div className="tnfLine">
                                    <Calendar size={16} />
                                    <div className="tnfLine__content">
                                        <div className="tnfLine__label">Date dépense / soumission</div>
                                        <div className="tnfLine__value">
                                            {formatDate(selectedNote.dateDepense)} — soumise le {formatDate(selectedNote.createdAt)}
                                        </div>
                                    </div>
                                </div>

                                <div className="tnfLine">
                                    <DollarSign size={16} />
                                    <div className="tnfLine__content">
                                        <div className="tnfLine__label">Montant demandé</div>
                                        <div className="tnfLine__value">{formatMontant(selectedNote.montantDepense)}</div>
                                    </div>
                                </div>

                                {/*<div className="tnfLine">*/}
                                {/*    <Briefcase size={16} />*/}
                                {/*    <div className="tnfLine__content">*/}
                                {/*        <div className="tnfLine__label">Centre de coût</div>*/}
                                {/*        <div className="tnfLine__value">{selectedNote.centreCouts || "-"}</div>*/}
                                {/*    </div>*/}
                                {/*</div>*/}

                                {selectedNote.ordreMissionLibelle && (
                                    <div className="tnfLine">
                                        <FileText size={16} />
                                        <div className="tnfLine__content">
                                            <div className="tnfLine__label">Ordre de mission</div>
                                            <div className="tnfLine__value">
                                                {selectedNote.ordreMissionCode} - {selectedNote.ordreMissionLibelle}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="tnfLine">
                                    <FileText size={16} />
                                    <div className="tnfLine__content">
                                        <div className="tnfLine__label">Description</div>
                                        <div className="tnfLine__value">{selectedNote.description || "-"}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Justificatifs */}
                            {selectedNote.justificatifs && selectedNote.justificatifs.length > 0 && (
                                <div className="tnfBlock">
                                    <div className="tnfBlock__title">Justificatifs ({selectedNote.justificatifs.length})</div>
                                    <div className="tnfDocs">
                                        {selectedNote.justificatifs.map((j, idx) => (
                                            <a key={idx} className="tnfDoc" href={getDownloadUrl(j)} target="_blank" rel="noreferrer">
                                                <FileText size={18} />
                                                <div className="tnfDoc__meta">
                                                    <div className="tnfDoc__name">{j}</div>
                                                </div>
                                                <Download size={16} className="tnfDoc__dl" />
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Historique de validation */}
                            {(selectedNote.status !== "PENDING_MANAGER") && (
                                <div className="tnfBlock">
                                    <div className="tnfBlock__title">Historique de validation</div>
                                    <div className="tnfPanel">
                                        <div className="tnfGrid2">
                                            <div className="tnfInfo">
                                                <div className="tnfInfo__label">Statut</div>
                                                <div className="tnfInfo__value">{getStatutBadge(selectedNote.status)}</div>
                                            </div>
                                            <div className="tnfInfo">
                                                <div className="tnfInfo__label">Date de validation</div>
                                                <div className="tnfInfo__value">{formatDate(selectedNote.managerDecisionAt)}</div>
                                            </div>
                                            <div className="tnfInfo">
                                                <div className="tnfInfo__label">Décision manager</div>
                                                <div className="tnfInfo__value">
                                                    {selectedNote.managerDecision ? (
                                                        <span style={{ color: "#155724" }}>✓ Approuvé</span>
                                                    ) : (
                                                        <span style={{ color: "#721c24" }}>✗ Rejeté</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {selectedNote.managerComment && (
                                            <div className="tnfRejectReason">
                                                <div className="tnfRejectReason__label">Commentaire manager</div>
                                                <div className="tnfRejectReason__text">{selectedNote.managerComment}</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Validation RH si applicable */}
                            {(selectedNote.status === "APPROVED" || selectedNote.status === "REJECTED" || selectedNote.status === "PAID") && selectedNote.adminDecisionAt && (
                                <div className="tnfBlock">
                                    <div className="tnfBlock__title">Validation RH</div>
                                    <div className="tnfPanel">
                                        <div className="tnfGrid2">
                                            <div className="tnfInfo">
                                                <div className="tnfInfo__label">Admin RH</div>
                                                <div className="tnfInfo__value">
                                                    {selectedNote.adminName || "-"}
                                                </div>
                                            </div>
                                            <div className="tnfInfo">
                                                <div className="tnfInfo__label">Date validation</div>
                                                <div className="tnfInfo__value">{formatDate(selectedNote.adminDecisionAt)}</div>
                                            </div>
                                            {selectedNote.montantValide && (
                                                <div className="tnfInfo">
                                                    <div className="tnfInfo__label">Montant validé</div>
                                                    <div className="tnfInfo__value tnfValueOk">
                                                        {formatMontant(selectedNote.montantValide)}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {selectedNote.adminComment && (
                                            <div className="tnfRejectReason">
                                                <div className="tnfRejectReason__label">Commentaire RH</div>
                                                <div className="tnfRejectReason__text">{selectedNote.adminComment}</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="tnfModal__footer">
                            {activeTab === "enCours" && (
                                <>
                                    <button
                                        type="button"
                                        className="tnfBtn tnfBtn--ghostDanger"
                                        onClick={() => {
                                            closeDetails();
                                            handleReject(selectedNote);
                                        }}
                                    >
                                        Rejeter
                                    </button>

                                    <button
                                        type="button"
                                        className="tnfBtn tnfBtn--ok"
                                        onClick={() => {
                                            handleApprove(selectedNote.id);
                                            closeDetails();
                                        }}
                                    >
                                        Approuver
                                    </button>
                                </>
                            )}

                            <button type="button" className="tnfBtn tnfBtn--ghost" onClick={closeDetails}>
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* REJECT MODAL */}
            {showRejectModal && selectedNote && (
                <div className="tnfModal">
                    <div className="tnfModal__panel tnfModal__panel--small" role="dialog" aria-modal="true">
                        <div className="tnfModal__header">
                            <div className="tnfModal__headLeft">
                                <div className="tnfModal__icon tnfModal__icon--danger">
                                    <AlertCircle size={18} />
                                </div>
                                <div>
                                    <div className="tnfModal__title">Refuser la note de frais</div>
                                    <div className="tnfModal__sub">
                                        {selectedNote.employeeName || "N/A"}
                                    </div>
                                </div>
                            </div>

                            <button type="button" className="tnfIconBtn" onClick={closeReject} aria-label="Fermer">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tnfModal__body">
                            <div className="tnfMiniCard">
                                <div className="tnfMiniRow">
                                    <span className="tnfMuted">Catégorie :</span>
                                    <span className="tnfStrong">
                                        {selectedNote.category || selectedNote.requestType || "-"}
                                    </span>
                                </div>
                                <div className="tnfMiniRow">
                                    <span className="tnfMuted">Montant :</span>
                                    <span className="tnfStrong tnfStrong--accent">
                                        {formatMontant(selectedNote.montantDepense)}
                                    </span>
                                </div>
                                <div className="tnfMiniRow">
                                    <span className="tnfMuted">Description :</span>
                                    <span className="tnfStrong">{selectedNote.description || "-"}</span>
                                </div>

                                {selectedNote.urgent && (
                                    <div className="tnfMiniWarn">
                                        <AlertCircle size={16} />
                                        <span>Note de frais urgente</span>
                                    </div>
                                )}
                            </div>

                            <label className="tnfLabel">
                                Motif du rejet <span className="tnfReq">*</span>
                            </label>
                            <textarea
                                value={motifRejet}
                                onChange={(e) => setMotifRejet(e.target.value)}
                                rows={5}
                                placeholder="Veuillez préciser le motif du rejet de cette note de frais..."
                                className="tnfTextarea"
                            />
                            <div className="tnfHelp">Ce motif sera communiqué au collaborateur</div>
                        </div>

                        <div className="tnfModal__footer">
                            <button type="button" className="tnfBtn tnfBtn--ghost" onClick={closeReject}>
                                Annuler
                            </button>
                            <button
                                type="button"
                                className="tnfBtn tnfBtn--danger"
                                onClick={confirmReject}
                                disabled={!motifRejet.trim() || loading}
                            >
                                {loading ? "En cours..." : "Confirmer le rejet"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TraitementNotesFraisManager;
